import React, { useState, useEffect } from "react";
import { Box, Text, Stack, Progress, HStack } from "@chakra-ui/react";

const DataExecutionFees = () => {
  const [data, setData] = useState([]);
  const [ethPrice, setEthPrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiUrl = "https://blindfoldcapital.com/api/execution_fees/";

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US");
  };

  const fetchData = async () => {
    try {
      const [feesRes, priceRes] = await Promise.all([
        fetch(apiUrl),
        fetch(
          "https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd",
        ),
      ]);
      if (!feesRes.ok || !priceRes.ok) throw new Error("Network error");

      const feesData = await feesRes.json();
      const priceData = await priceRes.json();

      setData(feesData.data);
      setEthPrice(priceData.ethereum.usd);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error}</p>;

  const totalExecutions = data.length;
  const totalEthSpent = totalExecutions * 0.00002;
  const totalUsdSpent = ethPrice ? totalEthSpent * ethPrice : null;

  const startTimestamp = new Date(data[0]?.timestamp).getTime();
  const endTimestamp = new Date(data[data.length - 1]?.timestamp).getTime();
  const minutesElapsed = (endTimestamp - startTimestamp) / (1000 * 60);
  const hoursElapsed = (endTimestamp - startTimestamp) / (1000 * 60 * 60);
  const daysElapsed = (endTimestamp - startTimestamp) / (1000 * 60 * 60 * 24); // ✅ added days

  const tpm =
    minutesElapsed > 0 ? (totalExecutions / minutesElapsed).toFixed(2) : "N/A";
  const tph =
    hoursElapsed > 0 ? (totalExecutions / hoursElapsed).toFixed(2) : "N/A";
  const cpm =
    minutesElapsed > 0 && totalUsdSpent
      ? (totalUsdSpent / minutesElapsed).toFixed(4)
      : "N/A";
  const cph =
    hoursElapsed > 0 && totalUsdSpent
      ? (totalUsdSpent / hoursElapsed).toFixed(2)
      : "N/A";
  const cpd =
    daysElapsed > 0 && totalUsdSpent
      ? (totalUsdSpent / daysElapsed).toFixed(2)
      : "N/A"; // ✅ new

  const startDate = formatDate(data[0]?.timestamp);
  const endDate = formatDate(data[data.length - 1]?.timestamp);

  return (
    <Box
      p={2}
      fontFamily="Sentinel-light"
      borderRadius="md"
      bg="gray.50"
      boxShadow="md"
      maxW="500px"
      mx="auto"
    >
      <Stack spacing={0}>
        <Text fontSize="md" fontWeight="bold">
          Execution Fee Summary
        </Text>
        <Text fontSize="sm" color="gray.600">
          From: {startDate} → To: {endDate}
        </Text>
        <Text fontSize="sm">Total Trades: {totalExecutions}</Text>
        <Text fontSize="sm">
          Trades: {tph} TPH / {tpm} TPM
        </Text>
        <Text fontSize="sm">
          Cost: ${cph} CPH / ${cpm} CPM
        </Text>
        <Text fontSize="sm">Cost per Day: ${cpd} CPD</Text>

        <HStack>
          <Text fontSize="sm">
            Total Execution Fees: {totalEthSpent.toFixed(4)} ETH
          </Text>
          <Text fontSize="sm">(${totalUsdSpent.toFixed(2)})</Text>
        </HStack>
        {totalUsdSpent && (
          <>
            <Progress
              value={Math.min(totalUsdSpent, 100)}
              max={100}
              size="sm"
              colorScheme="blue"
            />
          </>
        )}
      </Stack>
    </Box>
  );
};

export default DataExecutionFees;
